<template>
  <div class="flex flex-col gap-4">
    <MessageComponent v-if="loadError && showBillingSettings" color="danger">
      {{ $p("cannot-load-invoice-settings") }}
    </MessageComponent>
    <MessageComponent
      v-if="noViewPermission && !loadError"
      color="info"
      class="scroll-mt-4"
    >
      {{ $p("cannot-view-invoice-settings") }}
    </MessageComponent>
    <MessageComponent
      v-if="noEditPermission"
      ref="noEditPermissionMessageRef"
      color="danger"
      class="scroll-mt-4"
    >
      {{ $p("cannot-edit-invoice-settings") }}
    </MessageComponent>
    <div class="grid gap-4 min-[2048px]:grid-cols-3 xl:grid-cols-2">
      <template v-if="showBillingSettings">
        <!-- E-mail -->
        <BaseCard class="flex flex-col gap-4 p-6">
          <!-- Card header -->
          <div class="flex justify-between">
            <TextSubtitle>Contactgegevens</TextSubtitle>
            <BaseButton
              v-if="!editingInvoiceEmail"
              plain
              small
              :disabled="!!invoiceEmailSent || noViewPermission"
              @click="editingInvoiceEmail = true"
            >
              Wijzigen
            </BaseButton>
            <BaseButton
              v-else
              plain
              small
              class="text-danger!"
              @click="cancelEditingInvoiceEmail"
            >
              Annuleer
            </BaseButton>
          </div>
          <div class="flex max-w-96 flex-col gap-4">
            <!-- E-mail -->
            <form @submit.prevent>
              <InputField
                v-model="invoiceSettings.email"
                type="email"
                label="E-mail"
                :state-error="validInvoiceEmailError"
                :helper-text="
                  validInvoiceEmailError ? 'Ongeldig e-mailadres' : null
                "
                :disabled="
                  !editingInvoiceEmail ||
                  invoiceEmailPending ||
                  !!invoiceEmailSent
                "
                @blur="showInvoiceEmailValidation = true"
              >
                <template #action>
                  <BaseButton
                    v-if="editingInvoiceEmail"
                    color="primary"
                    :disabled="
                      invoiceSettings.email === invoicesStore.invoiceEmail
                    "
                    :loading="invoiceEmailPending"
                    @click="submitInvoiceEmailRequest"
                  >
                    Wijzig
                  </BaseButton>
                </template>
              </InputField>
            </form>
            <MessageComponent v-if="invoiceEmailSent" in-card>
              <p>
                Er is een bevestigingsmail verstuurd naar
                <span class="font-bold">{{ invoiceEmailSent }}</span
                >. Gebruik de link in de mail om het nieuwe e-mailadres te
                bevestigen.
              </p>
            </MessageComponent>
          </div>
        </BaseCard>
        <!-- IBAN -->
        <BaseCard class="flex flex-col gap-4 p-6">
          <!-- Card header -->
          <div class="flex justify-between">
            <TextSubtitle>Rekening</TextSubtitle>
            <BaseButton
              v-if="!editingIban"
              plain
              small
              :disabled="noViewPermission"
              @click="editingIban = true"
            >
              Wijzigen
            </BaseButton>
            <BaseButton
              v-else
              plain
              small
              class="text-danger!"
              @click="cancelEditingIban"
            >
              Annuleer
            </BaseButton>
          </div>
          <div class="flex max-w-96 flex-col gap-4">
            <!-- IBAN -->
            <form @submit.prevent>
              <InputField
                v-model="invoiceSettings.iban"
                type="text"
                label="IBAN"
                :state-error="invalidIbanError"
                :helper-text="
                  invalidIbanError ? 'Geen geldig IBAN nummer' : null
                "
                :disabled="!editingIban || ibanPending"
                @blur="showIbanValidation = true"
              >
                <template #action>
                  <BaseButton
                    v-if="editingIban"
                    color="primary"
                    :disabled="invoiceSettings.iban === invoicesStore.iban"
                    :loading="ibanPending"
                    @click="submitIbanRequest"
                  >
                    Wijzig
                  </BaseButton>
                </template>
              </InputField>
            </form>
            <MessageComponent v-if="ibanSent" in-card>
              <p>
                Het rekeningnummer is gewijzigd naar:
                <span class="font-bold"> {{ ibanSent }}</span>
              </p>
            </MessageComponent>
          </div>
        </BaseCard>
        <!-- Address -->
        <BaseCard class="flex flex-col gap-4 p-6">
          <!-- Card header -->
          <div class="flex justify-between">
            <TextSubtitle>Adresgegevens</TextSubtitle>
            <BaseButton
              v-if="!editingAddress"
              plain
              small
              :disabled="noViewPermission"
              @click="editingAddress = true"
            >
              Wijzigen
            </BaseButton>
            <BaseButton
              v-else
              plain
              small
              class="text-danger!"
              @click="cancelEditingAddress"
            >
              Annuleer
            </BaseButton>
          </div>
          <!-- Address -->
          <form @submit.prevent>
            <div class="flex max-w-96 flex-col gap-4">
              <InputField
                v-for="field in addressFields"
                :key="field.key"
                v-model="invoiceSettings[field.key]"
                type="text"
                :label="field.label"
                :disabled="!editingAddress || addressPending"
              />
            </div>
            <BaseButton
              v-if="editingAddress"
              color="primary"
              :disabled="!addressChanged"
              :loading="addressPending"
              class="mt-4"
              @click="submitInvoiceAddress"
            >
              Wijzig
            </BaseButton>
          </form>
          <MessageComponent v-if="addressSent" in-card>
            <div class="flex flex-col">
              <p>Het adres is gewijzigd naar:</p>
              <div class="grid grid-cols-[max-content_1fr] gap-x-4">
                <span>Straat:</span>
                <span class="font-bold">{{
                  addressSent.streetNameAndNumber
                }}</span>
                <span>Toevoeging:</span>
                <span class="font-bold">{{
                  addressSent.buildingApartment
                }}</span>
                <span>Postcode:</span>
                <span class="font-bold">{{ addressSent.postalCode }}</span>
                <span>Woonplaats:</span>
                <span class="font-bold">{{ addressSent.city }}</span>
                <span>Land:</span>
                <span class="font-bold">{{ addressSent.country }}</span>
              </div>
            </div>
          </MessageComponent>
        </BaseCard>
      </template>

      <!-- Mandate -->
      <MandateStatusPanel />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, reactive, watch, nextTick } from "vue"
import HTTP_STATUS from "@/helpers/httpStatus.js"
import $p from "@/services/phraseService.js"
import { triggerEvent } from "@/services/plausibleService.js"
import useNotificationStore from "@/stores/notificationStore.js"
import useUserStore from "@/stores/userStore.js"
import useInvoicesStore from "@/stores/repower/invoicesStore.js"
import useFilterStore from "@/stores/filterStore.js"
import {
  TextSubtitle,
  BaseCard,
  BaseButton,
  InputField,
} from "@repowerednl/ui-component-library"
import MessageComponent from "@/components/information/MessageComponent.vue"
import MandateStatusPanel from "@/components/panels/MandateStatusPanel.vue"

const notificationStore = useNotificationStore()
const userStore = useUserStore()
const invoicesStore = useInvoicesStore()
const filterStore = useFilterStore()
const showBillingSettings = userStore.hasFlag("can-see-billing-settings")

const loadError = ref(false)
const noViewPermission = ref(false)
const noEditPermission = ref(false)
const noEditPermissionMessageRef = ref()

const editingInvoiceEmail = ref(false)
const showInvoiceEmailValidation = ref(false)
const invoiceEmailPending = ref(false)
const invoiceEmailSent = ref(false)

const editingIban = ref(false)
const showIbanValidation = ref(false)
const ibanPending = ref(false)
const ibanSent = ref(false)
const ibanRefused = ref(false)

const editingAddress = ref(false)
const addressPending = ref(false)
const addressSent = ref(false)

const invoiceSettings = reactive({
  email: "",
  iban: "",
  streetNameAndNumber: "",
  buildingApartment: "",
  postalCode: "",
  city: "",
  country: "",
})

const addressFields = [
  { key: "streetNameAndNumber", label: "Straatnaam huisnummer en toevoeging" },
  { key: "buildingApartment", label: "Bijzondere toevoeging" },
  { key: "postalCode", label: "Postcode" },
  { key: "city", label: "Woonplaats" },
  { key: "country", label: "Land" },
]

function resetDrafts() {
  invoiceSettings.email = invoicesStore.invoiceEmail
  invoiceSettings.iban = invoicesStore.iban
  invoiceSettings.streetNameAndNumber = invoicesStore.streetNameAndNumber
  invoiceSettings.buildingApartment = invoicesStore.buildingApartment
  invoiceSettings.postalCode = invoicesStore.postalCode
  invoiceSettings.city = invoicesStore.city
  invoiceSettings.country = invoicesStore.country
}

function showCannotEditMessage() {
  noEditPermission.value = true
  nextTick(() => {
    noEditPermissionMessageRef.value.$el.scrollIntoView({ behavior: "smooth" })
  })
}

const invalidIbanError = computed(() => {
  return showIbanValidation.value && (!validIban.value || ibanRefused.value)
})

const validIban = computed(() => {
  // A simple regex to validate masked IBAN format
  const ibanRegex = /^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/
  return ibanRegex.test(invoiceSettings.iban.replace(/\s+/g, "").toUpperCase())
})

const validInvoiceEmail = computed(() => {
  // This pattern should match the one used in the 'validate_email()' function
  // in the 'repower-django' backend.
  const emailPattern = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,7}\b/
  return invoiceSettings.email.match(emailPattern)
})

const validInvoiceEmailError = computed(() => {
  return showInvoiceEmailValidation.value && !validInvoiceEmail.value
})

function cancelEditingInvoiceEmail() {
  editingInvoiceEmail.value = false
  invoiceSettings.email = invoicesStore.invoiceEmail
  invoiceEmailPending.value = false
  showInvoiceEmailValidation.value = false
}

function submitInvoiceEmailRequest() {
  if (!validInvoiceEmail.value) {
    return
  }
  invoiceEmailPending.value = true
  invoicesStore.requestInvoiceEmailChange(
    filterStore.currentPortfolio.id,
    invoiceSettings.email,
    () => {
      invoiceEmailSent.value = invoiceSettings.email
      notificationStore.pushToast(
        "E-mailadreswijziging aangevraagd",
        "Er is een e-mail verstuurd naar het nieuwe adres ter bevestiging.",
        "email-change-success",
      )
      cancelEditingInvoiceEmail()
    },
    (error) => {
      cancelEditingInvoiceEmail()
      if (error.code === HTTP_STATUS.FORBIDDEN) {
        showCannotEditMessage()
      } else {
        invoiceEmailPending.value = false
        notificationStore.pushError(
          "Fout bij het wijzigen van het factuur e-mailadres",
          `Het e-mailadres kon niet worden gewijzigd (code: ${error.code}).`,
          "invoice-email-change-error",
        )
      }
    },
  )
  triggerEvent("invoice:request-email-change")
}

function cancelEditingIban() {
  editingIban.value = false
  invoiceSettings.iban = invoicesStore.iban
  ibanPending.value = false
  showIbanValidation.value = false
  ibanRefused.value = false
}

function submitIbanRequest() {
  if (!validIban.value) {
    return
  }
  ibanPending.value = true

  invoicesStore.changeIban(
    filterStore.currentPortfolio.id,
    invoiceSettings.iban,
    () => {
      ibanSent.value = invoiceSettings.iban
      notificationStore.pushToast(
        "IBAN gewijzigd",
        "Het IBAN nummer is gewijzigd.",
        "iban-change-success",
      )
      cancelEditingIban()
    },
    (error) => {
      ibanPending.value = false
      if (error.code === HTTP_STATUS.FORBIDDEN) {
        showCannotEditMessage()
        cancelEditingIban()
      } else if (error.code === HTTP_STATUS.BAD_REQUEST) {
        showIbanValidation.value = true
        ibanRefused.value = true
      } else {
        notificationStore.pushError(
          "Fout bij het wijzigen van het IBAN nummer",
          `Het IBAN nummer kon niet worden gewijzigd (code: ${error.code}).`,
          "invoice-iban-change-error",
        )
        cancelEditingIban()
      }
    },
  )
  triggerEvent("invoice:iban-change")
}

const addressChanged = computed(() => {
  const fields = [
    "streetNameAndNumber",
    "buildingApartment",
    "postalCode",
    "city",
    "country",
  ]

  return fields.some((key) => invoiceSettings[key] !== invoicesStore[key])
})

function cancelEditingAddress() {
  editingAddress.value = false
  invoiceSettings.streetNameAndNumber = invoicesStore.streetNameAndNumber
  invoiceSettings.buildingApartment = invoicesStore.buildingApartment
  invoiceSettings.postalCode = invoicesStore.postalCode
  invoiceSettings.city = invoicesStore.city
  invoiceSettings.country = invoicesStore.country
  addressPending.value = false
}

function submitInvoiceAddress() {
  addressPending.value = true
  invoicesStore.changeInvoiceAddress(
    filterStore.currentPortfolio.id,
    {
      streetNameAndNumber: invoiceSettings.streetNameAndNumber,
      buildingApartment: invoiceSettings.buildingApartment,
      postalCode: invoiceSettings.postalCode,
      city: invoiceSettings.city,
      country: invoiceSettings.country,
    },
    () => {
      addressSent.value = {
        streetNameAndNumber: invoiceSettings.streetNameAndNumber,
        buildingApartment: invoiceSettings.buildingApartment,
        postalCode: invoiceSettings.postalCode,
        city: invoiceSettings.city,
        country: invoiceSettings.country,
      }
      addressPending.value = false
      notificationStore.pushToast(
        "Adres gewijzigd",
        "Het adres is gewijzigd.",
        "address-change-success",
      )
      cancelEditingAddress()
    },
    (error) => {
      addressPending.value = false
      cancelEditingAddress()
      if (error.code === HTTP_STATUS.FORBIDDEN) {
        showCannotEditMessage()
      } else {
        notificationStore.pushError(
          "Fout bij het wijzigen van het adres",
          `Het adres kon niet worden gewijzigd (code: ${error.code}).`,
          "invoice-address-change-error",
        )
      }
    },
  )
  triggerEvent("invoice:address-change")
}

function onLoadInvoiceSettingsFailed(error) {
  noViewPermission.value = true
  if (error.code !== HTTP_STATUS.FORBIDDEN) {
    loadError.value = true
  }
}

watch(
  () => invoicesStore.isInvoiceSettingsLoaded,
  (loaded) => {
    if (loaded) {
      resetDrafts()
    }
  },
  { immediate: true },
)

watch(
  () => filterStore.currentPortfolio,
  (portfolio) => {
    if (portfolio) {
      // Reset messages
      noViewPermission.value = false
      noEditPermission.value = false
      loadError.value = false
      // Reset form states
      ibanSent.value = false
      invoiceEmailSent.value = false
      addressSent.value = false
      cancelEditingInvoiceEmail()
      cancelEditingIban()
      cancelEditingAddress()
      // Load invoice settings
      invoicesStore.loadInvoiceSettings(
        portfolio.id,
        null,
        onLoadInvoiceSettingsFailed,
      )
    } else {
      invoicesStore.resetInvoiceSettings()
    }
  },
  { immediate: true },
)
</script>
